import {
  AreaType,
  CrashSeverity,
  IntxTypeSignalOrStop,
  RoundaboutRampSpfType,
  SafetyIntxType,
  SignalOrStopRampTerminal,
  SignalizedRampTerminal,
  StopControlRampTerminal,
} from "../SPFConstants";
import {
  getNumberExpectedCrashes,
  getKValueForRoundabout,
  getKValueForSignalOrStopRampTerminal,
} from "../spfHelpers";
import {
  getCMF10,
  getCMF11,
  getCMF12,
  getCMF14,
  getCMF15,
} from "./rampCMFHelper";
import {
  getSpfForSignalizedRampterminal,
  getSpfForOneWaySCRampterminal,
  getSpfForRoundaboutRampTerminal,
} from "./rampSPFHelper";
import {
  getCMF13,
  getCMF16,
  getCMF17,
  getCMF18,
  getCMF19,
} from "./signalControlRampCMFHelper";
import { getCMF20 } from "./stopControlRampCMFHelper";

interface getSignalCmfsProps extends SignalizedRampTerminal {
  crashSeverity: CrashSeverity.FATAL_INJURY | CrashSeverity.PDO;
}
const getSignalCmfs = (props: getSignalCmfsProps): number => {
  return (
    getCMF11(props) *
    getCMF12(props) *
    getCMF13(props) *
    getCMF14(props) *
    getCMF15(props) *
    getCMF16(props) *
    getCMF17(props) *
    getCMF18(props) *
    getCMF19(props) *
    (props.crashSeverity === CrashSeverity.FATAL_INJURY ? getCMF10(props) : 1)
  );
};

interface getStopControlCmfsProps extends StopControlRampTerminal {
  crashSeverity: CrashSeverity.FATAL_INJURY | CrashSeverity.PDO;
}
const getStopControlCmfs = (props: getStopControlCmfsProps): number => {
  return (
    getCMF11(props) *
    getCMF12(props) *
    (props.crashSeverity === CrashSeverity.FATAL_INJURY
      ? getCMF10(props) * getCMF14(props) * getCMF15(props) * getCMF20(props)
      : 1)
  );
};

const getPennDotCalibrationFactor = (
  crashSeverity: CrashSeverity.FATAL_INJURY | CrashSeverity.PDO,
  intxType: IntxTypeSignalOrStop
): number => {
  if (crashSeverity === CrashSeverity.FATAL_INJURY) {
    return intxType === SafetyIntxType.DIAMOND_WITH_SIGNALS ? 0.67 : 1.37;
  }
  return intxType === SafetyIntxType.DIAMOND_WITH_SIGNALS ? 0.49 : 1.04;
};

const getPredictedCrashes = (
  props: SignalOrStopRampTerminal | RoundaboutRampSpfType
): number => {
  if (props.intxType === SafetyIntxType.DIAMOND_WITH_ONE_LANE_ROUNDABOUTS) {
    return getSpfForRoundaboutRampTerminal(props);
  }

  return (
    getPennDotCalibrationFactor(props.crashSeverity, props.intxType) *
    (props.intxType === SafetyIntxType.DIAMOND_WITH_SIGNALS
      ? getSpfForSignalizedRampterminal(props) * getSignalCmfs(props)
      : getSpfForOneWaySCRampterminal(props) * getStopControlCmfs(props))
  );
};

/**
 * @returns number of fatal and injury predicted crashes for a ramp terminal signal, stop, or one lane roundabout
 */
export const getFatalInjuryPredicted = (
  props: SignalOrStopRampTerminal | RoundaboutRampSpfType
): number => {
  return getPredictedCrashes({
    ...props,
    crashSeverity: CrashSeverity.FATAL_INJURY,
  });
};

/**
 * @returns number of Property Damage Only predicted crashes for a ramp terminal signal, stop, or one lane roundabout
 */
const getPdoPredicted = (
  props: SignalOrStopRampTerminal | RoundaboutRampSpfType
): number => {
  return getPredictedCrashes({
    ...props,
    crashSeverity: CrashSeverity.PDO,
  });
};

/**
 * @returns number of total predicted crashes for a ramp terminal signal, stop, or one lane roundabout
 */
export const getTotalPredicted = (
  props: SignalOrStopRampTerminal | RoundaboutRampSpfType
): number => {
  if (props.intxType === SafetyIntxType.DIAMOND_WITH_ONE_LANE_ROUNDABOUTS) {
    return getPredictedCrashes({
      ...props,
      crashSeverity: CrashSeverity.TOTAL,
    });
  }

  return getFatalInjuryPredicted(props) + getPdoPredicted(props);
};

const getTotalExpectedForRoundabout = (props: RoundaboutRampSpfType) => {
  const { areaType, yearsOfAnalysis, observedTotalCrashes } = props;
  return getNumberExpectedCrashes({
    k: getKValueForRoundabout(CrashSeverity.TOTAL, areaType === AreaType.URBAN),
    predictedCrashes: getTotalPredicted(props),
    yearsOfAnalysis,
    observedCrashes: observedTotalCrashes,
  });
};

/**
 * @returns number of fatal and injury expected crashes for a ramp terminal signal, stop, or one lane roundabout
 */
export const getFatalInjuryExpected = (
  props: SignalOrStopRampTerminal | RoundaboutRampSpfType
): number => {
  const { intxType, observedFatalInjuryCrashes, yearsOfAnalysis } = props;

  if (intxType === SafetyIntxType.DIAMOND_WITH_ONE_LANE_ROUNDABOUTS) {
    const totalExpected = getTotalExpectedForRoundabout(props);
    return getSpfForRoundaboutRampTerminal({
      ...props,
      crashSeverity: CrashSeverity.FATAL_INJURY,
      totalCrashes: totalExpected,
    });
  }

  return getNumberExpectedCrashes({
    k: getKValueForSignalOrStopRampTerminal(
      CrashSeverity.FATAL_INJURY,
      intxType
    ),
    predictedCrashes: getFatalInjuryPredicted(props),
    yearsOfAnalysis,
    observedCrashes: observedFatalInjuryCrashes,
  });
};

/**
 * @returns number of Property Damage Only expected crashes for a ramp terminal signal or stop. Not needed for one lane roundabout ramp terminals
 */
const getPdoExpected = (props: SignalOrStopRampTerminal): number => {
  const {
    intxType,
    yearsOfAnalysis,
    observedFatalInjuryCrashes,
    observedTotalCrashes,
  } = props;
  return getNumberExpectedCrashes({
    k: getKValueForSignalOrStopRampTerminal(CrashSeverity.PDO, intxType),
    predictedCrashes: getPdoPredicted(props),
    yearsOfAnalysis,
    observedCrashes: observedTotalCrashes - observedFatalInjuryCrashes,
  });
};

/**
 * @returns number of total expected crashes for a ramp terminal signal, stop, or one lane roundabout
 */
export const getTotalExpected = (
  props: SignalOrStopRampTerminal | RoundaboutRampSpfType
): number => {
  if (props.intxType === SafetyIntxType.DIAMOND_WITH_ONE_LANE_ROUNDABOUTS) {
    return getTotalExpectedForRoundabout(props);
  }
  return getFatalInjuryExpected(props) + getPdoExpected(props);
};
